import { IconCheck } from "@/assets/icons/geist/IconCheck";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";
import { BusinessVATSection, type BusinessVATData } from "@/components/billing/BusinessVATSection";
import { PaymentMethodRequiredModal } from "@/components/billing/PaymentMethodRequiredModal";
import { Button } from "@/components/flexkit/Button";
import { Card } from "@/components/flexkit/Card";
import { formatShortDate } from "@/format/time";
import { IconTriangleExclamation } from "central-icons/IconTriangleExclamation";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading1 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { ENTERPRISE_TRIAL_REQUESTED } from "@/components/tiering/TierViolationDialog";
import { Switch } from "@/components/Switch";
import { useConfetti } from "@/hooks/use-confetti";
import { usePreferenceValue } from "@/hooks/use-preference-value";
import { TrackLocations, useSegmentTrack } from "@/hooks/use-segment";
import { getStripeAppearance, useStripePromise } from "@/hooks/use-stripe";
import { useTheme } from "@/hooks/use-theme";
import { useGetAccount } from "@/queries/account-queries";
import {
    useCreateCoreSubscription,
    useGetBillingInfo,
    useListSubscriptions,
    useSetBillingAddress,
    useCheckCouponCode,
    useCancelSubscription,
    type PlainGetCouponResponse,
} from "@/queries/organization-queries";
import {
    CORE_PLAN,
    CORE_PLAN_AMOUNT_OPTIONS,
    ENTERPRISE_PLAN,
    FREE_PLAN,
} from "@/routes/settings/organization/billing/billing-constants";
import { computeCoreSubscriptionStatus } from "@/routes/settings/organization/billing/billing-utils";
import { StripePaymentButton } from "@/routes/settings/organization/billing/StripePaymentButton";
import { formatError, isNoDefaultPaymentMethodError } from "@/utils/errors";
import { AddressElement, Elements, useElements } from "@stripe/react-stripe-js";
import type { StripeAddressElementChangeEvent } from "@stripe/stripe-js";
import {
    BillingAddress,
    PaymentMethodStatus,
    SubscriptionStatus,
    SubscriptionType,
} from "gitpod-next-api/gitpod/v1/billing_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { Input } from "@/components/podkit/forms/Input";
import IconMoneyHand from "central-icons/IconMoneyHand";
import { Banner } from "@/components/flexkit/Banner";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import { Pill } from "@/components/podkit/status/Pill";

type ChangeSubscriptionModalProps = {
    onOpenChange: (open: boolean) => void;
};
export const ChangeSubscriptionModal: FC<ChangeSubscriptionModalProps> = ({ onOpenChange }) => {
    const { toast } = useToast();
    const segmentTrack = useSegmentTrack();
    const { data: account } = useGetAccount();
    const confetti = useConfetti();

    const createCoreSubscription = useCreateCoreSubscription();
    const setBillingAddress = useSetBillingAddress();
    const { data: subscriptions } = useListSubscriptions();
    const { data: billingInfo } = useGetBillingInfo();
    const checkCoupon = useCheckCouponCode();
    const { data: couponData, isPending: isCheckingCoupon } = checkCoupon;
    const cancelSubscription = useCancelSubscription();

    const subscription = useMemo(() => {
        if (!subscriptions) {
            return undefined;
        }
        return computeCoreSubscriptionStatus(subscriptions);
    }, [subscriptions]);

    const isCoreSubscriptionActive = subscription?.status === SubscriptionStatus.ACTIVE;
    const isOnFreePlan = !isCoreSubscriptionActive;

    const { value: enterpriseTrialRequestedValue, setValueAsync: setEnterpriseTrialRequestedValue } =
        usePreferenceValue(ENTERPRISE_TRIAL_REQUESTED);
    const enterpriseTrialRequested = enterpriseTrialRequestedValue === "true";

    const [showChangeSubscriptionConfirmationModal, setShowChangeSubscriptionConfirmationModal] = useState(false);
    const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
    const [selectedAmount, setSelectedAmount] = useState<number>(CORE_PLAN_AMOUNT_OPTIONS[0].amountUsd);
    const [couponCode, setCouponCode] = useState<string>("");
    const [couponError, setCouponError] = useState<string>("");
    const [isFreePlanExpanded, setIsFreePlanExpanded] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

    const handleCheckCoupon = useCallback(async () => {
        if (!couponCode.trim()) {
            setCouponError("Please enter a coupon code");
            return;
        }

        setCouponError("");

        try {
            const result = await checkCoupon.mutateAsync(couponCode.trim());

            if (result === "invalid") {
                setCouponError("Invalid or expired coupon code");
            } else {
                setCouponError("");
            }
        } catch (error: unknown) {
            setCouponError(formatError(error));
        }
    }, [couponCode, checkCoupon]);

    const handleUpgradeToCore = useCallback(
        async (addressValue?: AddressValue, businessData?: BusinessVATData) => {
            try {
                if (addressValue?.value) {
                    const { address, name } = addressValue.value;
                    await setBillingAddress.mutateAsync({
                        address: new BillingAddress({
                            city: address.city,
                            country: address.country,
                            line1: address.line1,
                            line2: address.line2 ?? undefined,
                            postalCode: address.postal_code,
                            state: address.state,
                        }),
                        name,
                    });
                }

                await createCoreSubscription.mutateAsync({
                    amountUsd: selectedAmount,
                    couponCode: couponCode || undefined,
                    businessData,
                });
                segmentTrack("core_upgrade_completed", { amount: selectedAmount });
                confetti.dropConfetti();
                onOpenChange(false);
            } catch (error) {
                if (isNoDefaultPaymentMethodError(error)) {
                    setShowPaymentMethodModal(true);
                    return;
                }

                segmentTrack("core_upgrade_failed", {
                    error: formatError(error),
                    amount: selectedAmount,
                });
                toast({
                    title: "Failed to upgrade to Core",
                    description: formatError(error),
                });
            }
        },
        [
            createCoreSubscription,
            selectedAmount,
            couponCode,
            segmentTrack,
            confetti,
            onOpenChange,
            setBillingAddress,
            toast,
        ],
    );

    const handleVerificationAndUpgrade = useCallback(async () => {
        try {
            await createCoreSubscription.mutateAsync({
                amountUsd: selectedAmount,
                couponCode: couponCode || undefined,
            });
            segmentTrack("core_upgrade_completed", { amount: selectedAmount });
            confetti.dropConfetti();
            onOpenChange(false);
        } catch (error) {
            if (isNoDefaultPaymentMethodError(error)) {
                setShowPaymentMethodModal(true);
                return;
            }

            segmentTrack("core_upgrade_failed", {
                error: formatError(error),
                amount: selectedAmount,
            });
            toast({
                title: "Failed to upgrade to Core",
                description: formatError(error),
            });
        }
    }, [createCoreSubscription, confetti, onOpenChange, toast, segmentTrack, selectedAmount, couponCode]);

    const onEnterpriseTrialRequest = useCallback(async () => {
        try {
            segmentTrack("trial_requested", { type: "enterprise", email: account?.email });
            await setEnterpriseTrialRequestedValue("true");
            toast({
                title: "Trial requested!",
                description: "Our team is working on your request and will be in touch soon.",
            });
        } catch (error) {
            toast({
                title: "Failed to request trial",
                description: formatError(error),
            });
        }
    }, [account?.email, segmentTrack, setEnterpriseTrialRequestedValue, toast]);

    const needsCardVerification = !!billingInfo && billingInfo?.paymentMethodStatus !== PaymentMethodStatus.VERIFIED;

    const handleCancelSubscription = useCallback(async () => {
        try {
            await cancelSubscription.mutateAsync(SubscriptionType.CORE);
            onOpenChange(false);
        } catch (error) {
            console.error("Failed to cancel subscription:", error);
            toast({
                title: "Failed to cancel subscription",
                description: formatError(error),
            });
        }
    }, [cancelSubscription, onOpenChange, toast]);

    const handleDowngradeClick = useCallback(() => {
        if (!isOnFreePlan) {
            setShowCancelConfirmation(true);
        }
    }, [isOnFreePlan]);

    // Shared Free Plan Content Component
    const FreePlanPricing = () => (
        <PricingRow>
            <PriceDisplay>
                <span className="text-xl font-bold text-content-primary">$0</span>
                <Text className="ml-1 text-base text-content-secondary">/ month</Text>
            </PriceDisplay>
        </PricingRow>
    );

    const FreePlanBenefits = () => (
        <div className="flex-1">
            {showCancelConfirmation ? (
                <div className="flex flex-col gap-4">
                    <Text>
                        <b>Are you sure you&apos;d like to cancel your Core plan?</b> <br /> <br />
                        We understand that circumstances change, and we want to make this as easy as possible for you.
                    </Text>
                    <Text>What happens next:</Text>
                    <ul className="list-inside list-disc space-y-1 font-normal">
                        <li>Core features stay active until the end of your current billing period.</li>
                        <li>After that, your account will continue with our free plan features.</li>
                        <li>You can always resubscribe anytime.</li>
                    </ul>
                </div>
            ) : (
                <>
                    <BenefitListHeader>Includes:</BenefitListHeader>
                    <BenefitList benefits={FREE_PLAN.benefits} />
                </>
            )}
        </div>
    );

    const FreePlanButton = () => (
        <Button
            variant={isOnFreePlan ? "secondary" : showCancelConfirmation ? "destructive" : "outline"}
            className="w-full"
            disabled={isOnFreePlan}
            onClick={showCancelConfirmation ? handleCancelSubscription : handleDowngradeClick}
            loading={cancelSubscription.isPending}
            data-tracking-id={showCancelConfirmation ? "cancel-plan-free-tier" : "downgrade-to-free"}
        >
            {isOnFreePlan ? "Current plan" : showCancelConfirmation ? "Cancel Plan" : "Switch to Free"}
        </Button>
    );

    return (
        <Dialog open={true} onOpenChange={onOpenChange}>
            <DialogContent
                data-track-location={TrackLocations.ChangeSubscriptionModal}
                className="h-full max-h-full w-full max-w-full rounded-none border-0 bg-surface-primary p-6 lg:h-auto lg:max-h-[90%] lg:max-w-7xl lg:rounded-xl lg:border"
                data-testid="subscription-modal"
            >
                <DialogHeader className="mb-4">
                    <DialogTitle className="text-xl font-normal lg:text-lg">Choose your plan</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <div className="flex flex-col gap-6 md:flex-row">
                        {/* Free Plan Card */}
                        <Card
                            className="flex w-full flex-col gap-6 border bg-surface-secondary p-6 md:w-1/3"
                            variant="bordered"
                        >
                            {/* Mobile: Collapsible 
                                TODO: Consider using Radix UI Accordion component when available.
                                Current implementation uses custom collapse with proper accessibility attributes.
                            */}
                            <div className="md:hidden">
                                <button
                                    onClick={() => setIsFreePlanExpanded(!isFreePlanExpanded)}
                                    className="flex w-full items-center justify-between"
                                    aria-expanded={isFreePlanExpanded}
                                    aria-controls="free-plan-content"
                                    aria-label="Toggle Free plan details"
                                    data-tracking-id="toggle-free-plan-details"
                                >
                                    <div className="flex items-center gap-2">
                                        <Heading1 className="mb-0">Free</Heading1>
                                        {isOnFreePlan && (
                                            <Pill variant="success" size="md">
                                                Current
                                            </Pill>
                                        )}
                                    </div>
                                    <IconChevronDownSmall
                                        className={cn(
                                            "transition-transform duration-200",
                                            isFreePlanExpanded && "rotate-180",
                                        )}
                                        aria-hidden="true"
                                    />
                                </button>

                                <Text className="mt-2 text-content-secondary">Great for getting started</Text>

                                <div
                                    id="free-plan-content"
                                    className={cn(
                                        "flex flex-col gap-6 overflow-hidden transition-all duration-300 ease-out",
                                        isFreePlanExpanded
                                            ? "mt-6 max-h-[2000px] opacity-100"
                                            : "mt-0 max-h-0 opacity-0",
                                    )}
                                    aria-hidden={!isFreePlanExpanded}
                                >
                                    <FreePlanPricing />
                                    <FreePlanBenefits />
                                    <FreePlanButton />
                                </div>
                            </div>

                            {/* Desktop: Always expanded */}
                            <div className="hidden md:flex md:flex-1 md:flex-col md:gap-6">
                                {/* Header and pricing container with 16px gap */}
                                <PlanSectionGroup>
                                    <PlanHeaderSection>
                                        <PlanTitleRow>
                                            <Heading1 className="mb-0">Free</Heading1>
                                            {isOnFreePlan && (
                                                <Pill variant="success" size="md">
                                                    Current
                                                </Pill>
                                            )}
                                        </PlanTitleRow>
                                        <Text className="text-content-secondary">Great for getting started</Text>
                                    </PlanHeaderSection>

                                    <FreePlanPricing />
                                </PlanSectionGroup>

                                <FreePlanBenefits />

                                <FreePlanButton />
                            </div>
                        </Card>

                        {/* Core Plan Card */}
                        <CorePlanCard
                            isCoreSubscriptionActive={isCoreSubscriptionActive}
                            selectedAmount={selectedAmount}
                            setSelectedAmount={setSelectedAmount}
                            couponCode={couponCode}
                            setCouponCode={setCouponCode}
                            couponError={couponError}
                            setCouponError={setCouponError}
                            couponData={couponData}
                            isCheckingCoupon={isCheckingCoupon}
                            handleCheckCoupon={handleCheckCoupon}
                            checkCoupon={checkCoupon}
                            needsCardVerification={needsCardVerification}
                            createCoreSubscription={createCoreSubscription}
                            onOpenChange={onOpenChange}
                            segmentTrack={segmentTrack}
                            setShowChangeSubscriptionConfirmationModal={setShowChangeSubscriptionConfirmationModal}
                            showChangeSubscriptionConfirmationModal={showChangeSubscriptionConfirmationModal}
                            handleUpgradeToCore={handleUpgradeToCore}
                            handleVerificationAndUpgrade={handleVerificationAndUpgrade}
                        />

                        {/* Enterprise Plan Card */}
                        <Card
                            className="flex w-full flex-col gap-6 border bg-surface-secondary p-6 md:w-1/3"
                            variant="bordered"
                        >
                            {/* Header and pricing container with 16px gap */}
                            <PlanSectionGroup>
                                <PlanHeaderSection>
                                    <Heading1 className="mb-1">Enterprise</Heading1>
                                    <Text className="text-content-secondary">
                                        Maximum security, compliance and control
                                    </Text>
                                </PlanHeaderSection>

                                <PricingRow>
                                    <Text className="text-xl font-medium">Custom pricing</Text>
                                </PricingRow>
                            </PlanSectionGroup>

                            <div className="flex-1">
                                <BenefitListHeader>Everything in Core, plus:</BenefitListHeader>
                                <BenefitList benefits={ENTERPRISE_PLAN.benefits} />
                            </div>

                            <Button
                                variant="secondary"
                                disabled={enterpriseTrialRequested}
                                className="w-full"
                                onClick={onEnterpriseTrialRequest}
                                data-tracking-id="enterprise-trial-request-change-subscription-modal"
                            >
                                {enterpriseTrialRequested ? "Requested trial" : "Request Trial"}
                            </Button>
                        </Card>
                    </div>
                    <div className="mt-4 text-center">
                        <a
                            href="https://ona.com/pricing"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-content-secondary underline hover:text-content-primary"
                        >
                            Learn more about our plans
                        </a>
                    </div>
                </DialogBody>
            </DialogContent>
            <PaymentMethodRequiredModal
                open={showPaymentMethodModal}
                onClose={() => setShowPaymentMethodModal(false)}
            />
        </Dialog>
    );
};

type AddressValue = Pick<StripeAddressElementChangeEvent, "complete" | "isNewAddress" | "value">;

// Core Plan Card Component
type CorePlanCardProps = {
    isCoreSubscriptionActive: boolean;
    selectedAmount: number;
    setSelectedAmount: (amount: number) => void;
    couponCode: string;
    setCouponCode: (code: string) => void;
    couponError: string;
    setCouponError: (error: string) => void;
    couponData: PlainGetCouponResponse | "invalid" | undefined;
    isCheckingCoupon: boolean;
    handleCheckCoupon: () => Promise<void>;
    checkCoupon: ReturnType<typeof useCheckCouponCode>;
    needsCardVerification: boolean;
    createCoreSubscription: ReturnType<typeof useCreateCoreSubscription>;
    onOpenChange: (open: boolean) => void;
    segmentTrack: ReturnType<typeof useSegmentTrack>;
    setShowChangeSubscriptionConfirmationModal: (show: boolean) => void;
    showChangeSubscriptionConfirmationModal: boolean;
    handleUpgradeToCore: (addressValue?: AddressValue, businessData?: BusinessVATData) => Promise<void>;
    handleVerificationAndUpgrade: () => Promise<void>;
};

const CorePlanCard: FC<CorePlanCardProps> = ({
    isCoreSubscriptionActive,
    selectedAmount,
    setSelectedAmount,
    couponCode,
    setCouponCode,
    couponError,
    setCouponError,
    couponData,
    isCheckingCoupon,
    handleCheckCoupon,
    checkCoupon,
    needsCardVerification,
    createCoreSubscription,
    onOpenChange,
    segmentTrack,
    setShowChangeSubscriptionConfirmationModal,
    showChangeSubscriptionConfirmationModal,
    handleUpgradeToCore,
    handleVerificationAndUpgrade,
}) => {
    return (
        <Card
            className="relative flex w-full flex-col gap-6 border bg-surface-secondary p-6 shadow-lg md:w-1/3"
            variant="bordered"
        >
            {/* Header and pricing container with 16px gap */}
            <PlanSectionGroup>
                <PlanHeaderSection>
                    <PlanTitleRow>
                        <Heading1 className="mb-0">Core</Heading1>
                        {isCoreSubscriptionActive && (
                            <Pill variant="success" size="md">
                                Current
                            </Pill>
                        )}
                    </PlanTitleRow>
                    <Text className="text-content-secondary">Ideal for individual users and teams</Text>
                </PlanHeaderSection>

                <PricingRow>
                    {/* Plan selector */}
                    <Select
                        value={selectedAmount.toString()}
                        onValueChange={(value) => setSelectedAmount(Number(value))}
                    >
                        <SelectTrigger className="w-full" loading={false}>
                            <PriceDisplay>
                                <Text className="text-xl font-bold">${selectedAmount}</Text>
                                <Text className="ml-1 text-base text-content-secondary">
                                    / month for{" "}
                                    {CORE_PLAN_AMOUNT_OPTIONS.find((opt) => opt.amountUsd === selectedAmount)?.label}
                                </Text>
                            </PriceDisplay>
                        </SelectTrigger>
                        <SelectContent>
                            {CORE_PLAN_AMOUNT_OPTIONS.map((option) => (
                                <SelectItem key={option.amountUsd} value={option.amountUsd.toString()}>
                                    <PriceDisplay>
                                        <Text className="text-xl font-semibold">${option.amountUsd}</Text>
                                        <Text className="ml-1 text-base">/ month for {option.label}</Text>
                                    </PriceDisplay>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </PricingRow>
            </PlanSectionGroup>

            <div className="flex-1">
                <BenefitListHeader>Everything in Free, plus:</BenefitListHeader>
                <BenefitList benefits={CORE_PLAN.benefits} />
            </div>

            {/* Promotional Banner and Coupon Input */}
            <div className="flex flex-col gap-3">
                {/* Promotional Banner */}
                <Banner
                    variant="info"
                    text="$100 in free credits applied automatically when you upgrade for the first time."
                    LeadingIcon={<IconMoneyHand />}
                    className="text-content-primary [&_[data-testid='banner-text']]:break-normal"
                />

                {/* Coupon Code Input */}
                <div className="space-y-2">
                    <div
                        className={cn("group flex gap-2", couponError && "data-[state=error]")}
                        data-state={couponError ? "error" : undefined}
                    >
                        <Input
                            type="text"
                            placeholder="Do you have a coupon code?"
                            value={couponCode}
                            onChange={(e) => {
                                setCouponCode(e.target.value);
                                setCouponError("");
                                checkCoupon.reset();
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && couponCode.trim() && !isCheckingCoupon) {
                                    e.preventDefault();
                                    void handleCheckCoupon();
                                }
                            }}
                            className={cn(
                                "flex-1",
                                couponError && "border-border-error",
                                couponData && "border-border-success",
                            )}
                            aria-label="Do you have a coupon code?"
                            suffix={
                                couponData ? (
                                    <IconCheck size="sm" className="h-4 w-4 text-content-success" />
                                ) : undefined
                            }
                        />
                        <Button
                            variant="secondary"
                            onClick={handleCheckCoupon}
                            disabled={!couponCode.trim() || isCheckingCoupon}
                            loading={isCheckingCoupon}
                            data-tracking-id="apply-coupon"
                        >
                            Apply
                        </Button>
                    </div>

                    {/* Error feedback */}
                    {couponError && (
                        <div className="flex items-center gap-2 rounded-md border border-border-warning bg-surface-warning-subtle p-3">
                            <IconTriangleExclamation size={16} className="shrink-0 text-content-warning" />
                            <Text className="text-sm font-medium text-content-warning">{couponError}</Text>
                        </div>
                    )}

                    {/* Success feedback */}
                    {couponData && couponData !== "invalid" && (
                        <div className="flex items-center gap-2 rounded-md border border-border-success bg-surface-success-subtle p-3">
                            <IconCircleCheck size={16} className="shrink-0 text-content-success" />
                            <Text className="text-sm font-medium text-content-success">
                                Coupon applied successfully!
                            </Text>
                        </div>
                    )}
                </div>
            </div>

            {isCoreSubscriptionActive ? (
                <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() => onOpenChange(false)}
                    data-tracking-id="stay-on-core"
                >
                    Stay on Core
                </Button>
            ) : (
                <>
                    {needsCardVerification ? (
                        <StripePaymentButton
                            onSuccess={handleVerificationAndUpgrade}
                            submitButtonText={`Subscribe for $${selectedAmount}/month + tax`}
                            dialogTitle="Upgrade to Core"
                            data-tracking-id="upgrade-to-core-with-verification-change-subscription-modal"
                        >
                            {({ isLoading, onAddPayment }) => (
                                <Button
                                    variant="brand"
                                    className="w-full"
                                    loading={isLoading || createCoreSubscription.isPending}
                                    onClick={() => {
                                        segmentTrack("core_upgrade_started");
                                        onAddPayment();
                                    }}
                                    data-testid="stripe-payment-button"
                                    data-tracking-id="upgrade-to-core"
                                >
                                    Upgrade to Core
                                </Button>
                            )}
                        </StripePaymentButton>
                    ) : (
                        <Button
                            variant="brand"
                            className="w-full"
                            loading={createCoreSubscription.isPending}
                            onClick={() => {
                                segmentTrack("core_upgrade_started");
                                setShowChangeSubscriptionConfirmationModal(true);
                            }}
                            data-tracking-id="upgrade-to-core-change-subscription-modal"
                        >
                            Upgrade to Core
                        </Button>
                    )}

                    {showChangeSubscriptionConfirmationModal && (
                        <StripeWrapper>
                            <SubscriptionConfirmationModal
                                onClose={() => setShowChangeSubscriptionConfirmationModal(false)}
                                onSubscribe={handleUpgradeToCore}
                                selectedAmount={selectedAmount}
                                couponCode={couponCode}
                                couponData={couponData}
                            />
                        </StripeWrapper>
                    )}
                </>
            )}
        </Card>
    );
};

type SubscriptionConfirmationModalProps = {
    onClose: () => void;
    onSubscribe: (addressValue?: AddressValue, businessData?: BusinessVATData) => void;
    selectedAmount: number;
    couponCode?: string;
    couponData?: PlainGetCouponResponse | "invalid";
};
export const SubscriptionConfirmationModal: FC<SubscriptionConfirmationModalProps> = ({
    onClose,
    onSubscribe,
    selectedAmount,
    couponCode,
    couponData,
}: SubscriptionConfirmationModalProps) => {
    const elements = useElements();

    const [withBillingAddress, setWithBillingAddress] = useState<boolean>(true);
    const [businessVATData, setBusinessVATData] = useState<BusinessVATData>({
        isPurchasingAsBusiness: false,
        taxIdTypeId: "",
        taxIdValue: "",
    });

    const onOpenChange = (open: boolean) => {
        if (!open) {
            onClose();
        }
    };

    const handleChangeSubscription = useCallback(async () => {
        let value;
        if (withBillingAddress) {
            const addressElement = elements?.getElement(AddressElement);
            if (!addressElement) return;
            value = await addressElement.getValue();
            if (!value.complete) {
                return;
            }
        }

        onSubscribe(value, businessVATData);
        onClose();
    }, [withBillingAddress, onSubscribe, onClose, elements, businessVATData]);

    const handleBillingAddressToggle = (checked: boolean) => {
        setWithBillingAddress(checked);
    };

    return (
        <Dialog open onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="change-subscription-confirmation-modal"
                className="max-w-lg"
                data-track-location={TrackLocations.ChangeSubscriptionConfirmationModal}
            >
                <DialogHeader>
                    <DialogTitle>Subscribe to Core</DialogTitle>
                    <DialogDescription className="space-y-4">
                        {/* Subscription Preview Section */}
                        <div className="border-border-secondary rounded-lg border bg-surface-secondary p-4">
                            <Text className="mb-3 font-semibold">Your subscription includes:</Text>

                            {/* Plan Details */}
                            <div className="space-y-2">
                                <DetailRow>
                                    <Text className="text-content-secondary">Core Plan</Text>
                                    <Text className="font-medium">${selectedAmount}/month</Text>
                                </DetailRow>

                                <DetailRow>
                                    <Text className="text-content-secondary">Credits</Text>
                                    <Text className="font-medium">
                                        {selectedAmount === 500 ? (
                                            <>
                                                {selectedAmount * 4 + 200} OCUs{" "}
                                                <span className="text-sm text-content-secondary">
                                                    (includes 200 bonus)
                                                </span>
                                            </>
                                        ) : (
                                            <>{selectedAmount * 4} OCUs</>
                                        )}
                                    </Text>
                                </DetailRow>

                                {/* Coupon Details if present */}
                                {couponCode && couponData && couponData !== "invalid" && couponData.coupon && (
                                    <>
                                        <DetailDivider />
                                        <div className="space-y-2">
                                            <DetailRow>
                                                <Text className="font-medium text-content-success">Coupon Applied</Text>
                                                <Text className="font-mono text-sm text-content-success">
                                                    {couponCode}
                                                </Text>
                                            </DetailRow>

                                            {couponData.coupon.config?.percentageOff &&
                                            Number(couponData.coupon.config.percentageOff) > 0 ? (
                                                <DetailRow>
                                                    <Text className="text-sm text-content-secondary">Discount</Text>
                                                    <Text className="text-sm font-medium text-content-success">
                                                        {couponData.coupon.config.percentageOff.toString()}% off
                                                    </Text>
                                                </DetailRow>
                                            ) : null}

                                            {couponData.coupon.config?.oneTimeOcuGrant &&
                                            Number(couponData.coupon.config.oneTimeOcuGrant) > 0 ? (
                                                <DetailRow>
                                                    <Text className="text-sm text-content-secondary">Bonus OCUs</Text>
                                                    <Text className="text-sm font-medium text-content-success">
                                                        +{couponData.coupon.config.oneTimeOcuGrant.toString()} OCUs
                                                    </Text>
                                                </DetailRow>
                                            ) : null}

                                            {couponData.coupon.expiresAt && (
                                                <DetailRow>
                                                    <Text className="text-sm text-content-secondary">Valid until</Text>
                                                    <Text className="text-sm text-content-secondary">
                                                        {formatShortDate(
                                                            new Date(
                                                                Number(couponData.coupon.expiresAt.seconds) * 1000,
                                                            ),
                                                            true,
                                                        )}
                                                    </Text>
                                                </DetailRow>
                                            )}
                                        </div>
                                    </>
                                )}

                                <DetailDivider />

                                {/* Final pricing */}
                                <DetailRow>
                                    <Text className="font-semibold">Total (before tax)</Text>
                                    <Text className="font-semibold">
                                        {couponData &&
                                        couponData !== "invalid" &&
                                        couponData.coupon?.config?.percentageOff &&
                                        Number(couponData.coupon.config.percentageOff) > 0 ? (
                                            <>
                                                <span className="text-content-secondary line-through">
                                                    ${selectedAmount}
                                                </span>{" "}
                                                <span className="text-content-success">
                                                    $
                                                    {Math.round(
                                                        selectedAmount *
                                                            (1 - Number(couponData.coupon.config.percentageOff) / 100),
                                                    )}
                                                    /month
                                                </span>
                                            </>
                                        ) : (
                                            <>${selectedAmount}/month</>
                                        )}
                                    </Text>
                                </DetailRow>
                            </div>
                        </div>

                        <Text className="text-sm text-content-secondary">
                            This will upgrade your organization to the Core plan. Tax will be calculated based on your
                            location.
                        </Text>
                    </DialogDescription>
                </DialogHeader>

                <DialogBody className="flex flex-col gap-4">
                    <div className="mb-2 flex items-center gap-3">
                        <Switch
                            state={withBillingAddress ? "checked" : "unchecked"}
                            onToggle={handleBillingAddressToggle}
                        />
                        <Text className="text-base font-medium">Billing address</Text>
                    </div>

                    <Text>Billing address details are mandatory for invoice completion.</Text>

                    {withBillingAddress && <AddressForm />}

                    <div className="mt-4">
                        <BusinessVATSection data={businessVATData} onDataChange={setBusinessVATData} />
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            data-tracking-id="cancel-change-subscription-modal"
                        >
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        autoFocus={true}
                        variant="brand"
                        onClick={handleChangeSubscription}
                        data-tracking-id="subscribe-subscription-confirmation-modal"
                    >
                        Subscribe
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const StripeLoadingModal: FC = () => {
    return (
        <Dialog open>
            <DialogContent
                data-testid="stripe-loading-modal"
                className="max-w-lg"
                data-track-location={TrackLocations.ChangeSubscriptionConfirmationModal}
            >
                <DialogHeader>
                    <DialogTitle>Subscribe to Core</DialogTitle>
                </DialogHeader>
                <DialogBody className="flex flex-col items-center justify-center py-12">
                    <IconSpinner className="animate-spin text-content-primary" size="lg" />
                    <Text className="mt-4 text-content-secondary">Loading payment form...</Text>
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
};

const StripeWrapper: FC<{ children: React.ReactNode }> = ({ children }) => {
    const { stripePromise, isLoading } = useStripePromise();
    const theme = useTheme();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const appearance = useMemo(() => getStripeAppearance(), [theme]);

    // Show loading state while Stripe is loading to prevent modal from not appearing on slow networks
    if (!stripePromise || isLoading) {
        return <StripeLoadingModal />;
    }
    return (
        <Elements stripe={stripePromise} options={{ loader: "always", appearance }}>
            {children}
        </Elements>
    );
};

const AddressForm: FC = () => {
    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
    }, []);

    return (
        <form onSubmit={handleSubmit}>
            <AddressElement
                id="address-element"
                options={{
                    mode: "billing",
                    autocomplete: { mode: "automatic" },
                    display: {
                        name: "full",
                    },
                }}
            />
        </form>
    );
};


const PlanSectionGroup = ({ children }: { children: React.ReactNode }) => (
    <div className="flex flex-col gap-4">{children}</div>
);

const PlanHeaderSection = ({ children }: { children: React.ReactNode }) => (
    <div className="flex min-h-[56px] flex-col justify-start">{children}</div>
);

const PlanTitleRow = ({ children }: { children: React.ReactNode }) => (
    <div className="mb-1 flex items-center gap-2">{children}</div>
);

const PricingRow = ({ children }: { children: React.ReactNode }) => (
    <div className="flex h-[44px] items-center">{children}</div>
);

const PriceDisplay = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-baseline">{children}</div>
);

const BenefitListHeader = ({ children }: { children: React.ReactNode }) => (
    <Text className="mb-4 font-semibold">{children}</Text>
);

const BenefitList = ({ benefits }: { benefits: readonly string[] }) => (
    <ul className="space-y-2">
        {benefits.map((benefit) => (
            <li key={benefit} className="flex items-start gap-2">
                <IconCheck size="base" className="flex-shrink-0" />
                <Text className="text-base">{benefit}</Text>
            </li>
        ))}
    </ul>
);

const DetailRow = ({ children }: { children: React.ReactNode }) => (
    <div className="flex items-center justify-between">{children}</div>
);

const DetailDivider = () => <div className="border-border-secondary my-2 border-t" />;
